import { useState, useCallback, useEffect } from 'react'
import { PageHeader, DataTable, ColumnSettingsButton, btnPrimary, btnDanger, colors } from '../../components/ui'
import type { Column } from '../../components/ui'
import { api, ApiError } from '../../services/api'
import { TenantSecretModal, SecretRevealModal, type TenantSecretRow } from './TenantSecretModal'
import { useTableColumns } from '../../hooks/use-table-columns'

function formatDate(dt: string | null): string {
  if (!dt) return '—'
  try {
    return new Date(dt).toLocaleDateString()
  } catch {
    return dt
  }
}

export function TenantSecretsPage() {
  const [secrets, setSecrets] = useState<readonly TenantSecretRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showCreate, setShowCreate] = useState(false)
  const [revealInfo, setRevealInfo] = useState<{ secretKey: string; value: string } | null>(null)
  const [rotating, setRotating] = useState<string | null>(null)
  const [deleting, setDeleting] = useState<string | null>(null)
  const [refreshKey, setRefreshKey] = useState(0)
  const { tableId: _, ...tableColumnProps } = useTableColumns('tenant-secrets')

  useEffect(() => {
    let cancelled = false
    api
      .get<readonly TenantSecretRow[]>('/tenant-secrets')
      .then((res) => {
        if (cancelled) return
        setSecrets(res.data ?? [])
        setLoading(false)
      })
      .catch((err) => {
        if (cancelled) return
        setError(err instanceof ApiError ? err.message : 'Failed to load secrets')
        setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [refreshKey])

  const handleCreated = useCallback((secretKey: string, value: string) => {
    setShowCreate(false)
    setRevealInfo({ secretKey, value })
    setRefreshKey((k) => k + 1)
  }, [])

  const handleRotate = useCallback((key: string) => {
    const newValue = prompt('Enter new value for ' + key + ':')
    if (!newValue) return
    setRotating(key)
    api
      .put(`/tenant-secrets/${encodeURIComponent(key)}`, { value: newValue })
      .then(() => {
        setRotating(null)
        setRefreshKey((k) => k + 1)
      })
      .catch((err) => {
        setError(err instanceof ApiError ? err.message : 'Failed to rotate secret')
        setRotating(null)
      })
  }, [])

  const handleDelete = useCallback((key: string) => {
    if (!confirm(`Delete secret "${key}"? EHR configs referencing it will fail at runtime.`)) return
    setDeleting(key)
    api
      .delete(`/tenant-secrets/${encodeURIComponent(key)}`)
      .then(() => {
        setDeleting(null)
        setRefreshKey((k) => k + 1)
      })
      .catch((err) => {
        setError(err instanceof ApiError ? err.message : 'Failed to delete secret')
        setDeleting(null)
      })
  }, [])

  const columns: readonly Column<TenantSecretRow>[] = [
    {
      key: 'secretKey',
      header: 'Key',
      sortable: true,
      render: (r) => <code style={{ fontSize: '0.8125rem', fontWeight: 600 }}>{r.secretKey}</code>,
    },
    {
      key: 'description',
      header: 'Description',
      render: (r) => r.description ?? <span style={{ color: colors.textMuted }}>—</span>,
    },
    {
      key: 'createdAt',
      header: 'Created',
      sortable: true,
      render: (r) => formatDate(r.createdAt),
    },
    {
      key: 'lastAccessedAt',
      header: 'Last Accessed',
      render: (r) => formatDate(r.lastAccessedAt),
    },
    {
      key: 'lastRotatedAt',
      header: 'Last Rotated',
      render: (r) => formatDate(r.lastRotatedAt),
    },
    {
      key: 'id',
      header: '',
      render: (r) => (
        <div style={{ display: 'flex', gap: '4px', justifyContent: 'flex-end' }}>
          <button
            style={{ ...btnPrimary, fontSize: '0.75rem', padding: '3px 10px' }}
            disabled={rotating === r.secretKey}
            onClick={(e) => {
              e.stopPropagation()
              handleRotate(r.secretKey)
            }}
          >
            {rotating === r.secretKey ? 'Rotating...' : 'Rotate'}
          </button>
          <button
            style={{ ...btnDanger, fontSize: '0.75rem', padding: '3px 10px' }}
            disabled={deleting === r.secretKey}
            onClick={(e) => {
              e.stopPropagation()
              handleDelete(r.secretKey)
            }}
          >
            {deleting === r.secretKey ? 'Deleting...' : 'Delete'}
          </button>
        </div>
      ),
    },
  ]

  return (
    <div style={{ padding: '1.5rem' }}>
      <PageHeader
        title="Credential Vault"
        subtitle="Manage encrypted secrets referenced by EHR configurations (tenant_secret:KEY_NAME)"
        actions={
          <>
            <ColumnSettingsButton columns={columns} {...tableColumnProps} />
            <button style={btnPrimary} onClick={() => setShowCreate(true)} data-testid="add-secret-btn">
              Add Secret
            </button>
          </>
        }
      />

      {error && (
        <div
          style={{
            background: '#fee2e2',
            border: '1px solid #fca5a5',
            borderRadius: '6px',
            padding: '0.625rem 0.875rem',
            marginBottom: '1rem',
            fontSize: '0.875rem',
            color: '#991b1b',
          }}
        >
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ padding: '2rem', textAlign: 'center', color: colors.textMuted }}>Loading...</div>
      ) : (
        <DataTable
          columns={columns}
          rows={secrets as TenantSecretRow[]}
          keyField="id"
          emptyMessage="No secrets yet. Add one to store credentials for EHR integrations."
          {...tableColumnProps}
        />
      )}

      {showCreate && <TenantSecretModal onClose={() => setShowCreate(false)} onCreated={handleCreated} />}

      {revealInfo && (
        <SecretRevealModal
          secretKey={revealInfo.secretKey}
          value={revealInfo.value}
          onClose={() => setRevealInfo(null)}
        />
      )}
    </div>
  )
}
